<template>
  <section v-if="visibleAlerts.length" class="voucher-sequence-notice" data-header-popover
    aria-label="Aviso de reposición de comprobantes" aria-live="polite">
    <button type="button" class="voucher-sequence-notice-summary"
      :aria-label="visibleAlerts.length === 1 && canManage ? `Reponer comprobante ${visibleAlerts[0].series}` : 'Consultar comprobantes pendientes'"
      @click="openNotice">
      <span class="voucher-sequence-notice-line">
        <strong class="voucher-sequence-notice-heading">
          {{ visibleAlerts.length === 1 ? visibleAlerts[0].series : `${visibleAlerts.length} comprobantes con alerta` }}
        </strong>
        <VoucherReplenishmentStatus v-if="visibleAlerts.length === 1" compact
          :exhausted="visibleAlerts[0].exhausted" :remaining="visibleAlerts[0].remaining" />
      </span>
      <span class="voucher-sequence-notice-guidance">
        {{ visibleAlerts.length === 1 && canManage ? 'Abrir comprobante' : 'Consultar en la campanita' }}
      </span>
    </button>
    <button type="button" class="voucher-sequence-notice-control" aria-label="Ver notificaciones de comprobantes"
      @click="showNotifications">
      <svg class="w-5 h-5" aria-hidden="true"><use href="@/assets/svg/icon.svg#bell" /></svg>
    </button>
    <button type="button" class="voucher-sequence-notice-control voucher-sequence-notice-close"
      aria-label="Cerrar aviso de comprobantes" @click="dismiss">×</button>
  </section>
</template>

<script>
import { ref, watch, onUnmounted } from "vue";
import { createVoucherNoticePreferences } from "@/utils/voucherNoticePreferences";
import VoucherReplenishmentStatus from "../PublicComponents/VoucherReplenishmentStatus.vue";

export default {
  name: "VoucherSequenceNotice",
  components: { VoucherReplenishmentStatus },
  props: {
    alerts: { type: Array, required: true },
    canManage: Boolean,
    ownerKey: { type: String, default: "" },
    panelOpen: Boolean,
  },
  emits: ["replenish", "show-notifications", "visibility-change"],
  setup(props, { emit }) {
    const visibleAlerts = ref([]);
    let owner;
    let preferences;
    let timer = null;
    const clearTimer = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };
    const dismiss = () => {
      preferences.dismiss(visibleAlerts.value);
      visibleAlerts.value = [];
      clearTimer();
    };
    watch(() => [props.alerts, props.ownerKey, props.panelOpen], () => {
      if (owner !== props.ownerKey) {
        clearTimer();
        owner = props.ownerKey;
        preferences = createVoucherNoticePreferences(owner, () => window.localStorage);
      }
      visibleAlerts.value = props.alerts.filter(alert => !preferences.isDismissed(alert));
      if (props.panelOpen) {
        dismiss();
      } else if (!visibleAlerts.value.length) {
        clearTimer();
      } else if (timer === null) {
        timer = window.setTimeout(dismiss, 10000);
      }
    }, { immediate: true, deep: true });
    watch(() => visibleAlerts.value.length > 0, visible => emit("visibility-change", visible), { immediate: true });
    onUnmounted(clearTimer);

    const showNotifications = () => {
      dismiss();
      emit("show-notifications");
    };
    const openNotice = () => {
      if (visibleAlerts.value.length === 1 && props.canManage) {
        const alert = visibleAlerts.value[0];
        dismiss();
        emit("replenish", alert);
      } else {
        showNotifications();
      }
    };
    return { visibleAlerts, dismiss, openNotice, showNotifications };
  },
};
</script>

<style scoped>
.voucher-sequence-notice {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: inherit;
  background: #fef2f2;
  color: #991b1b;
  box-shadow: inset 0 -2px 0 #dc2626;
}
.voucher-sequence-notice-summary {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  height: 100%;
  text-align: left;
}
.voucher-sequence-notice-line { display: flex; align-items: center; gap: 8px; min-width: 0; }
.voucher-sequence-notice-heading {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 18px;
  font-weight: 700;
}
.voucher-sequence-notice-guidance { font-size: 12px; line-height: 16px; }
.voucher-sequence-notice-control {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 6px;
}
.voucher-sequence-notice-close { font-size: 24px; line-height: 1; }
.voucher-sequence-notice-control:hover { background: #fee2e2; }
.voucher-sequence-notice button:focus-visible {
  outline: 2px solid #991b1b;
  outline-offset: -2px;
}
</style>
